import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['walletStep', 'mintStep', 'progressStep', 'walletStatus', 'mintButton', 'loader', 'progressMessage', 'error', 'explorerLink']
  static values = { nftNumber: String, walletVerified: Boolean, verifiedAddress: String, nftContract: String }

  connect () {
    this.restoreWallet()
  }

  async restoreWallet () {
    if (!this.walletVerifiedValue || !globalThis.ethereum || !this.verifiedAddressValue) return
    const accounts = await globalThis.ethereum.request({ method: 'eth_accounts' })
    if (accounts[0]?.toLowerCase() === this.verifiedAddressValue.toLowerCase()) this.showMintStep(accounts[0])
  }

  async connectWallet () {
    try {
      if (!globalThis.ethereum) throw new Error('Install MetaMask to mint this NFT.')
      await this.ensureSepolia()
      const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
      const address = accounts[0]?.toLowerCase()
      if (!address) throw new Error('No MetaMask account was selected.')
      const challenge = await this.requestJson('/nfts/wallet_challenge', 'POST', { address })
      const signature = await globalThis.ethereum.request({ method: 'personal_sign', params: [challenge.message, address] })
      await this.requestJson('/nfts/wallet_connect', 'POST', { address, nonce: challenge.nonce, signature })
      this.walletVerifiedValue = true
      this.verifiedAddressValue = address
      this.showMintStep(address)
    } catch (error) {
      this.showError(error)
    }
  }

  async mint () {
    try {
      if (!this.nftContractValue) throw new Error('NFT contract is not configured.')
      const sender = await this.activeWallet()
      this.showProgress()
      this.setStage('metadata', 'active')
      const basePath = '/nfts/' + this.nftNumberValue
      const prepared = await this.requestJson(basePath + '/prepare_mint', 'POST', {})
      this.setStage('metadata', 'complete')
      this.setStage('mint', 'active')

      const transaction = {
        from: sender,
        to: prepared.nft_contract_address,
        data: this.encodeSafeMint(sender, prepared.metadata_uri)
      }
      transaction.gas = await this.estimateMintGas(transaction)
      const txHash = await globalThis.ethereum.request({
        method: 'eth_sendTransaction',
        params: [transaction]
      })
      const receipt = await this.waitForReceipt(txHash)
      const tokenId = this.tokenIdFromReceipt(receipt, prepared.nft_contract_address, sender)
      this.setStage('mint', 'complete')
      this.setStage('confirm', 'active')
      const confirmed = await this.requestJson(basePath + '/confirm_mint', 'PATCH', {
        tx_hash: txHash,
        contract_address: prepared.nft_contract_address,
        token_id: tokenId,
        metadata_uri: prepared.metadata_uri
      })
      this.setStage('confirm', 'complete')
      this.hideLoader()
      this.progressMessageTarget.textContent = 'Token #' + confirmed.token_id + ' is owned by ' + this.short(sender)
      this.explorerLinkTarget.href = confirmed.explorer_url
      this.explorerLinkTarget.classList.remove('d-none')
    } catch (error) {
      this.showError(error)
    }
  }

  async activeWallet () {
    await this.ensureSepolia()
    const accounts = await globalThis.ethereum.request({ method: 'eth_requestAccounts' })
    if (accounts[0]?.toLowerCase() !== this.verifiedAddressValue.toLowerCase()) {
      throw new Error('The active MetaMask account does not match the verified creator wallet.')
    }
    return accounts[0].toLowerCase()
  }

  async ensureSepolia () {
    const chainId = await globalThis.ethereum.request({ method: 'eth_chainId' })
    if (chainId.toLowerCase() !== '0xaa36a7') {
      await globalThis.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0xaa36a7' }] })
    }
  }

  encodeSafeMint (recipient, tokenUri) {
    const addressWord = recipient.replace(/^0x/, '').padStart(64, '0')
    const offsetWord = (64).toString(16).padStart(64, '0')
    const uriHex = Array.from(new TextEncoder().encode(tokenUri), (byte) => byte.toString(16).padStart(2, '0')).join('')
    const lengthWord = (uriHex.length / 2).toString(16).padStart(64, '0')
    return '0xd204c45e' + addressWord + offsetWord + lengthWord + uriHex.padEnd(Math.ceil(uriHex.length / 64) * 64, '0')
  }

  async estimateMintGas (transaction) {
    try {
      const estimate = BigInt(await globalThis.ethereum.request({
        method: 'eth_estimateGas',
        params: [transaction]
      }))
      const latestBlock = await globalThis.ethereum.request({
        method: 'eth_getBlockByNumber',
        params: ['latest', false]
      })
      const blockLimit = BigInt(latestBlock.gasLimit)
      const buffered = estimate + (estimate / 5n)
      if (buffered >= blockLimit) throw new Error('Estimated mint gas exceeds the Sepolia block limit.')
      return '0x' + buffered.toString(16)
    } catch (error) {
      if (error.message?.includes('block limit')) throw error
      throw new Error(
        'The configured ERC-721 contract rejected self-minting. Deploy ItoonsNFT.sol and update NFT_CONTRACT_ADDRESS.'
      )
    }
  }

  async waitForReceipt (txHash) {
    const deadline = Date.now() + 180000
    while (Date.now() < deadline) {
      const receipt = await globalThis.ethereum.request({ method: 'eth_getTransactionReceipt', params: [txHash] })
      if (receipt) {
        if (BigInt(receipt.status || '0x0') !== 1n) throw new Error('The Sepolia mint transaction reverted.')
        return receipt
      }
      await new Promise((resolve) => setTimeout(resolve, 2500))
    }
    throw new Error('Mint is still pending. Check Sepolia Etherscan.')
  }

  tokenIdFromReceipt (receipt, contractAddress, recipient) {
    const transferTopic = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
    const zeroTopic = '0x' + '0'.repeat(64)
    const recipientTopic = '0x' + recipient.replace(/^0x/, '').padStart(64, '0')
    const transfer = receipt.logs.find((log) =>
      log.address.toLowerCase() === contractAddress.toLowerCase() &&
      log.topics?.[0]?.toLowerCase() === transferTopic &&
      log.topics?.[1] === zeroTopic &&
      log.topics?.[2]?.toLowerCase() === recipientTopic
    )
    if (!transfer?.topics?.[3]) throw new Error('ERC-721 mint event was not found.')
    return BigInt(transfer.topics[3]).toString()
  }

  async requestJson (path, method, body) {
    const response = await fetch(path, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector("[name='csrf-token']").content },
      body: JSON.stringify(body)
    })
    const text = await response.text()
    const payload = text ? JSON.parse(text) : {}
    if (!response.ok) throw new Error(payload.errors?.join(', ') || payload.error || 'Request failed.')
    return payload
  }

  showMintStep (address) {
    this.walletStepTarget.classList.add('d-none')
    this.mintStepTarget.classList.remove('d-none')
    this.walletStatusTarget.textContent = this.short(address) + ' · Sepolia creator wallet'
  }

  showProgress () {
    this.mintStepTarget.classList.add('d-none')
    this.progressStepTarget.classList.remove('d-none')
    this.errorTarget.classList.add('d-none')
    this.showLoader('Preparing NFT metadata...')
  }

  setStage (name, state) {
    this.progressStepTarget.querySelector('[data-stage="' + name + '"]').dataset.status = state
    const messages = {
      metadata: 'Uploading media and metadata to IPFS...',
      mint: 'Confirm mint in MetaMask, then wait for Sepolia...',
      confirm: 'Verifying token ownership on Sepolia...'
    }
    if (state === 'active') this.showLoader(messages[name])
  }

  showError (error) {
    this.progressStepTarget.classList.remove('d-none')
    this.progressStepTarget.querySelector('[data-status="active"]')?.setAttribute('data-status', 'error')
    this.hideLoader()
    this.errorTarget.textContent = error.message || 'NFT mint failed.'
    this.errorTarget.classList.remove('d-none')
  }

  showLoader (message) {
    this.progressMessageTarget.textContent = message
    this.loaderTarget.classList.remove('d-none')
  }

  hideLoader () {
    this.loaderTarget.classList.add('d-none')
  }

  short (address) {
    return address.slice(0, 8) + '...' + address.slice(-6)
  }
}
